import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Flame } from 'lucide-react';
import { Icon } from '@/components/atoms/Icon';
import { usersAPI } from '@/services/api/users.api';
import { getUserStreak } from '@/services/api/gamification.api';
import { cn } from '@/utils/cn';
import { getWeekStartMs } from '@/pages/student/ProgressPage/utils/wordPeriodRanges';
import styles from './WeekStreakTracker.module.scss';
import type { WeekStreakTrackerProps } from './WeekStreakTracker.types';
import type { TFunction } from 'i18next';

/** YYYY-MM-DD en calendario local (alineado con celdas de la semana). */
function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Lunes → domingo de la semana calendario local (mismo lunes que `getWeekStartMs` en progreso). */
function getCalendarWeekDates(anchor = new Date()): Date[] {
  const startMs = getWeekStartMs(anchor);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(startMs);
    d.setHours(12, 0, 0, 0);
    d.setDate(d.getDate() + i);
    return d;
  });
}

interface StreakCopy {
  title: string;
  subtitle: string;
}

function getStreakCopy(
  currentStreak: number,
  longestStreak: number,
  isStreakAtRisk: boolean,
  t: TFunction
): StreakCopy {
  if (isStreakAtRisk && currentStreak > 0) {
    return {
      title: t('streak.riskTitle', { count: currentStreak }),
      subtitle: t('streak.riskSubtitle'),
    };
  }

  if (currentStreak === 0 && longestStreak > 0) {
    return {
      title: t('streak.startAgainTitle'),
      subtitle: t('streak.startAgainSubtitle', { count: longestStreak }),
    };
  }

  if (currentStreak === 0) {
    return {
      title: t('streak.noStreakTitle'),
      subtitle: t('streak.noStreakSubtitle'),
    };
  }

  if (currentStreak >= 30) {
    return {
      title: t('streak.tier30Title', { count: currentStreak }),
      subtitle: t('streak.tier30Subtitle'),
    };
  }

  if (currentStreak >= 14) {
    return {
      title: t('streak.tier14Title', { count: currentStreak }),
      subtitle: t('streak.tier14Subtitle'),
    };
  }

  if (currentStreak >= 7) {
    return {
      title: t('streak.tier7Title'),
      subtitle: t('streak.tier7Subtitle', { count: currentStreak }),
    };
  }

  if (currentStreak >= 4) {
    return {
      title: t('streak.tier4Title'),
      subtitle: t('streak.tier4Subtitle', { count: currentStreak }),
    };
  }

  return {
    title: t('streak.defaultTitle'),
    subtitle: t('streak.defaultSubtitle', { count: currentStreak }),
  };
}

export function WeekStreakTracker({ className }: WeekStreakTrackerProps) {
  const { t } = useTranslation();
  const [activeDates, setActiveDates] = useState<Set<string>>(new Set());
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [isStreakAtRisk, setIsStreakAtRisk] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [activities, streakData] = await Promise.all([
          usersAPI.getActivities(),
          getUserStreak(''),
        ]);

        const withActivity = new Set(
          activities.filter((a) => a.hasActivity).map((a) => a.date)
        );
        setActiveDates(withActivity);
        setCurrentStreak(streakData.currentStreak);
        setLongestStreak(streakData.longestStreak);
        setIsStreakAtRisk(streakData.isStreakAtRisk);
      } catch {
        setActiveDates(new Set());
        setCurrentStreak(0);
        setLongestStreak(0);
        setIsStreakAtRisk(false);
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, []);

  if (loading) {
    return (
      <div className={cn(styles.weekStreak, className)}>
        <div className={styles.weekStreak__message}>
          <p className={styles.weekStreak__subtitle}>{t('streak.loading')}</p>
        </div>
        <div className={styles.weekStreak__days}>
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className={styles.weekStreak__dayCard} aria-hidden>
              <span className={styles.weekStreak__dayCardSkeletonNum} />
              <span className={styles.weekStreak__dayCardSkeletonDow} />
              <div className={styles.weekStreak__dayCardSkeletonCircle} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const copy = getStreakCopy(currentStreak, longestStreak, isStreakAtRisk, t);

  const todayKey = toLocalDateKey(new Date());
  const days = getCalendarWeekDates(new Date());

  return (
    <div className={cn(styles.weekStreak, className)}>
      <div className={styles.weekStreak__message}>
        <h2 className={styles.weekStreak__title}>{copy.title}</h2>
        <p className={styles.weekStreak__subtitle}>{copy.subtitle}</p>
      </div>
      <div className={styles.weekStreak__days}>
        {days.map((d) => {
          const dateKey = toLocalDateKey(d);
          const hasActivity = activeDates.has(dateKey);
          const dayOfWeek = d.getDay();
          const dayOfMonth = String(d.getDate()).padStart(2, '0');
          const dowLabel = t(`streak.dayShort.${dayOfWeek}`);
          const isToday = dateKey === todayKey;
          const isFuture = dateKey > todayKey;

          const ariaLabel = hasActivity
            ? t('streak.dayAriaDone', { date: dateKey, day: dowLabel })
            : isToday
              ? t('streak.dayAriaTodayPending', { date: dateKey, day: dowLabel })
              : isFuture
                ? t('streak.dayAriaFuture', { date: dateKey, day: dowLabel })
                : t('streak.dayAriaMissed', { date: dateKey, day: dowLabel });

          return (
            <div
              key={dateKey}
              className={cn(
                styles.weekStreak__dayCard,
                isToday && !isFuture && styles['weekStreak__dayCard--today'],
                isFuture && styles['weekStreak__dayCard--future']
              )}
              aria-label={ariaLabel}
            >
              <span className={styles.weekStreak__dayCardNum}>{dayOfMonth}</span>
              <span className={styles.weekStreak__dayCardDow}>{dowLabel}</span>
              <div className={styles.weekStreak__dayCardStatus}>
                {hasActivity ? (
                  <div
                    className={cn(
                      styles.weekStreak__statusCircle,
                      styles['weekStreak__statusCircle--done']
                    )}
                  >
                    <Icon icon={Check} size="sm" className={styles.weekStreak__statusIcon} aria-hidden />
                  </div>
                ) : isToday ? (
                  <div
                    className={cn(
                      styles.weekStreak__statusCircle,
                      styles['weekStreak__statusCircle--today']
                    )}
                  >
                    <Flame size={18} className={styles.weekStreak__flameToday} aria-hidden strokeWidth={2} />
                  </div>
                ) : (
                  <div
                    className={cn(
                      styles.weekStreak__statusCircle,
                      isFuture
                        ? styles['weekStreak__statusCircle--future']
                        : styles['weekStreak__statusCircle--missed']
                    )}
                  >
                    <Flame
                      size={16}
                      className={isFuture ? styles.weekStreak__flameFuture : styles.weekStreak__flameMuted}
                      aria-hidden
                      strokeWidth={2}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

WeekStreakTracker.displayName = 'WeekStreakTracker';
